import {closeBurger, initBurger, openBurger} from "./_burger";
import {useState} from "../../utils/_state";
import {Expo, TimelineMax, TweenMax} from "gsap";
import {createCta, createItem, createSmallLink, createSubMenuHeader} from "./_item";
import {debounce} from "../../utils/_events";

const attributes = {
    dataAttribute: 'js-header-data',
    currentItemAttribute: 'js-header-current-item',
    hasChildrenAttribute: 'js-header-current-item-has-children',
    noLinkAttribute: 'js-header-no-link',
    idAttribute: 'id',
    backAttribute: 'js-header-back',
    linksAttribute: 'js-header-links',
    ctasAttribute: 'js-header-ctas'
}

const selectors = {
    headerSelector: '[js-header]',
    headerBarSelector: '[js-header-bar]',
    backgroundSelector: '[js-header-background]',
    menuSelector: '[js-header-menu]',
    subMenuSelector: '[js-header-submenu]',
    itemSelector: '[js-header-item]',
    mobileBarsSelector: '[js-header-mobile-bars]',
    dataSelector: `[${attributes.dataAttribute}]`,
    currentItemSelector: `[${attributes.currentItemAttribute}]`,
    hasChildrenSelector: `[${attributes.hasChildrenAttribute}]`,
    noLinkSelector: `[${attributes.noLinkAttribute}]`,
    linksSelector: `[${attributes.linksAttribute}]`,
    ctasSelector: `[${attributes.ctasAttribute}]`,
};

const animations = {
    header: {
        duration: .1,
        delay: 1.2,
        set: {
            in: {display: 'block'},
            out: {display: 'none'},
        }
    },
    items: {
        duration: .5,
        stagger: 0.05,
        delay: 1,
        in: {
            from: {opacity: 0, x: -30},
            to: {opacity: 1, x: 0}
        },
        out : {
            from: {opacity: 1, x: 0},
            to: {opacity: 0, x: -30}
        }
    },
    background: {
        duration: 1.2,
        in: {
            from: {scaleX: 0, ease: Expo.easeInOut},
            to: {scaleX: 1, ease: Expo.easeInOut}
        },
        out : {
            from: {scaleX: 1, ease: Expo.easeInOut},
            to: {scaleX: 0, ease: Expo.easeInOut}
        }
    },
    mobileBars: {
        duration: .5,
        delay: 1,
        in: {
            from: {opacity: 0, x: -30},
            to: {opacity: 1, x: 0}
        },
        out : {
            from: {opacity: 1, x: 0},
            to: {opacity: 0, x: -30}
        },
        set: {
            in: {display: 'flex'},
            out: {display: 'none'},
        }
    }
}

const dataDiv = document.querySelector(selectors.dataSelector)

const menuItems = dataDiv ? JSON.parse(dataDiv.getAttribute(attributes.dataAttribute)) : {};

const menu = document.querySelector(selectors.menuSelector);
const header = document.querySelector(selectors.headerSelector);
const headerBar = document.querySelector(selectors.headerBarSelector);

const menuLinks = dataDiv ? JSON.parse(dataDiv.getAttribute(attributes.linksAttribute)) : {};
const menuCtas = dataDiv ? JSON.parse(dataDiv.getAttribute(attributes.ctasAttribute)) : {};

const mobileBars = document.querySelectorAll(selectors.mobileBarsSelector);

dataDiv?.remove();

/**
 * States
 */
const [open, setOpen] = useState(false);
const [currentItems, setCurrentItems] = useState(null);

/**
 * On initialise le burger pour binder l'event click
 */
initBurger(debounce(() => {
    !open() ? openMenu() : closeMenu();
}, 1200, true));

document.addEventListener('click', ({target}) => {
    const isClickInside = header?.contains(target) || headerBar?.contains(target);
    if (!isClickInside && open()) {
        closeMenu()
    }
});

/**
 * Gère l'ouverture du menu
 */
const openMenu = () => {
    openBurger();
    createItems({items: menuItems, withBackground: true})
    setOpen(true)
}

/**
 * Gère la fermeture du menu
 */
const closeMenu = () => {
    closeBurger();
    removeItems({withBackground: true});
    setOpen(false)
}

const createItems = ({items, withBackground, previousItem}) => {
    //Si c'est un sous-menu
    if (previousItem) {
        createSubMenuHeader(previousItem, menu);
    }

    items.forEach(item => {
        createItem(item, menu, previousItem);
    });

    //Si c'est le menu de premier niveau
    if (!previousItem) {
        //On crée les liens Agenda / Actualités / Annuaires
        menuLinks.forEach(item => {
            createSmallLink(item,  menu);
        })

        //On crée les CTAs
        menuCtas.forEach(item => {
            createCta(item,  menu);
        })
    }

    setCurrentItems(document.querySelectorAll(selectors.currentItemSelector));

    /**
     * Prevent going to link if element has children
     */
    currentItems().forEach(item => item.addEventListener('click', el => changeSubmenu(el)));

    let animation = new TimelineMax({paused: true})
        .add([
            TweenMax.staggerFromTo(currentItems(), .5, animations.items.in.from, animations.items.in.to, animations.items.stagger)
        ]);

    if (withBackground) {
        animation = new TimelineMax({paused: true})
            .add([
                TweenMax.set(selectors.headerSelector, animations.header.set.in),
                TweenMax.set(mobileBars, animations.mobileBars.set.in),
                TweenMax.fromTo(selectors.backgroundSelector, animations.background.duration, animations.background.in.from, animations.background.in.to),
                TweenMax.fromTo(mobileBars, animations.mobileBars.duration, animations.mobileBars.in.from, animations.mobileBars.in.to).delay(animations.mobileBars.delay),
                TweenMax.staggerFromTo(currentItems(), animations.items.duration, animations.items.in.from, animations.items.in.to, animations.items.stagger).delay(animations.items.delay)
            ]);
    }

    animation.play()
}

const changeSubmenu = el => {
    if (el.target.hasAttribute(attributes.noLinkAttribute)) {
        el.preventDefault();

        const menuItem = menuItems.find(item => item.ID == el.target.getAttribute(attributes.idAttribute));

        removeItems({newItems: menuItem.children, previousItem: menuItem});
    }

    if (el.target.hasAttribute(attributes.backAttribute)) {
        const menuItem = menuItems.find(item => item.ID == el.target.getAttribute(attributes.idAttribute));

        //Amélioration possible : check le level pour récursivité
        removeItems({newItems: menuItems});
    }
}

const removeItems = ({newItems, withBackground, previousItem}) => {
    let animation = new TimelineMax({onComplete: () => onComplete(), paused: true})
        .add([
            TweenMax.staggerFromTo(currentItems(), animations.items.duration, animations.items.out.from, animations.items.out.to, animations.items.stagger)
        ]);

    if (withBackground) {
        animation = new TimelineMax({onComplete: () => onComplete(), paused: true})
            .add([
                TweenMax.staggerFromTo(currentItems(), animations.items.duration, animations.items.out.from, animations.items.out.to, animations.items.stagger),
                TweenMax.fromTo(selectors.backgroundSelector, animations.background.duration, animations.background.out.from, animations.background.out.to),
                TweenMax.fromTo(mobileBars, animations.mobileBars.duration, animations.mobileBars.out.from, animations.mobileBars.out.to),
                TweenMax.set(mobileBars, animations.mobileBars.set.out).delay(animations.header.delay),
                TweenMax.set(selectors.headerSelector, animations.header.set.out).delay(animations.header.delay),
            ]);
    }

    const onComplete = () => {
        currentItems().forEach(item => item.remove());
        if (newItems) {
            createItems({items: newItems, previousItem})
        }
    }

    animation.play();
}
